/**
 * Football camera kit — where the cameras are bolted down and what glass they carry.
 *
 * Coordinates follow core/contracts.ts (metres; x along length, z across, y up;
 * main gantry on the near side z < 0). NOTE: looking from the near-side gantry
 * toward +z, world -x appears on SCREEN RIGHT. "LEFT"/"RIGHT" camera names follow
 * the brief (LEFT = the x < 0 / home-goal end), not screen side.
 *
 * fovRange is VERTICAL field of view in degrees: [widest, tightest] (Three.js
 * PerspectiveCamera.fov convention). Values model real broadcast box lenses
 * (e.g. 86x–100x on the gantry, wide ENG zooms on the RF steadicams).
 */
import type { CameraDef, Vec3 } from '../../core/contracts';

const p = (x: number, y: number, z: number): Vec3 => ({ x, y, z });

export const FOOTBALL_CAMERA_KIT: CameraDef[] = [
  { id: 'CAM 1', kind: 'main_wide', label: 'MAIN WIDE', position: p(0, 22, -58), fovRange: [32, 6], reverse: false },
  { id: 'CAM 2', kind: 'main_tight', label: 'MAIN TIGHT', position: p(3.5, 22, -58), fovRange: [20, 1.6], reverse: false },
  { id: 'CAM 3', kind: 'high_18_left', label: 'HIGH 18 LEFT', position: p(-36, 18, -55), fovRange: [34, 2.5], reverse: false },
  { id: 'CAM 4', kind: 'high_18_right', label: 'HIGH 18 RIGHT', position: p(36, 18, -55), fovRange: [34, 2.5], reverse: false },
  { id: 'CAM 5', kind: 'behind_goal_left', label: 'BEHIND GOAL L', position: p(-66, 7, -3), fovRange: [45, 3], reverse: false },
  { id: 'CAM 6', kind: 'behind_goal_right', label: 'BEHIND GOAL R', position: p(66, 7, -3), fovRange: [45, 3], reverse: false },
  // Handheld mount positions are start points; operators walk the touchline/goal-line track.
  { id: 'CAM 7', kind: 'pitchside_handheld_left', label: 'HANDHELD L', position: p(-22, 1.6, -36.5), fovRange: [60, 6], reverse: false },
  { id: 'CAM 8', kind: 'pitchside_handheld_right', label: 'HANDHELD R', position: p(22, 1.6, -36.5), fovRange: [60, 6], reverse: false },
  { id: 'CAM 9', kind: 'reverse_angle', label: 'REVERSE', position: p(0, 20, 60), fovRange: [30, 2.5], reverse: true },
  { id: 'BENCH', kind: 'bench', label: 'BENCH', position: p(0, 1.7, -35.2), fovRange: [42, 5], reverse: false },
  { id: 'CROWD', kind: 'crowd', label: 'CROWD', position: p(14, 2.2, -37), fovRange: [35, 3], reverse: false },
  // Unlockables.
  { id: 'SPIDER', kind: 'spidercam', label: 'SPIDERCAM', position: p(0, 18, -14), fovRange: [55, 8], reverse: false, unlockId: 'spidercam' },
  { id: 'BLIMP', kind: 'blimp', label: 'BLIMP', position: p(-60, 160, -170), fovRange: [24, 1.5], reverse: false, unlockId: 'blimp' },
  { id: 'SSM', kind: 'super_slomo', label: 'SUPER SLO-MO', position: p(-6, 6, -42), fovRange: [22, 1.5], reverse: false, unlockId: 'super_slomo' },
];

/** Kit filtered by the player's unlocks (base cameras always included). */
export function kitFor(unlocked: Iterable<string> = []): CameraDef[] {
  const set = new Set(unlocked);
  return FOOTBALL_CAMERA_KIT.filter((c) => !c.unlockId || set.has(c.unlockId));
}

/**
 * Fallback world anchors used when the stadium module doesn't supply them.
 * Keys match the world module's `anchors` naming ('dugout_home', 'crowd_home_end', …).
 */
export const DEFAULT_CAMERA_ANCHORS: Record<string, Vec3> = {
  dugout_home: p(-9, 1.0, -38.5),
  dugout_away: p(9, 1.0, -38.5),
  crowd_home_end: p(-70, 9, 0),
  crowd_away_end: p(70, 9, 0),
  crowd_far: p(0, 11, 52),
  crowd_far_left: p(-35, 11, 52),
  crowd_far_right: p(35, 11, 52),
};
